import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
  Box,
} from "@material-ui/core";
import { CustomTheme } from "../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px",
      tableLayout: "fixed", // 🔹 distribute equal widths
      width: "100%", // 🔹 full width table
    },
    stickyWeekCell: {
      position: "sticky",
      left: 0,
      width: "120px", // keep week column a bit narrower
      minWidth: "120px",
      backgroundColor: theme.palette.background.default,
      zIndex: 20,
      fontWeight: "bold",
      borderBottom: "none",
    },
    headerCell: {
      fontWeight: "bold",
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },

    bodyRow: {
      backgroundColor: theme.palette.background.default,
    },
    tableWrapper: {
      overflowX: "auto",
    },
    yearHeader: {
      textAlign: "center",
      paddingBottom: theme.spacing(1),
      fontWeight: "bold",
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
    averageRow: {
      backgroundColor: theme.palette.background.paper,
      fontWeight: "bold",
      "& .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
  }),
);

interface WeeklyNode {
  card?: number;
  cash?: number;
  discount?: number;
  otherPayments?: number;
  saleCount: number;
  saleTotal: number;
  total?: number;
  week: string; // Example: "2024 December (Week 52)"
}

interface WeeklySalesTableProps {
  nodes: WeeklyNode[];
}

const ChartSalesTableWeekly: React.FC<WeeklySalesTableProps> = ({ nodes }) => {
  const theme: CustomTheme = useTheme();
  const classes = useStyles();

  const [weeklyAverages, setWeeklyAverages] = useState<any>({});
  const [weeklyTotalValues, setWeeklyTotalValues] = useState<number[]>([]);

  const monthOrder = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const weekRows = ["1st week", "2nd week", "3rd week", "4th week", "5th week"];

  const getWeekName = (weekNum: number): string => {
    const suffixes = ["th", "st", "nd", "rd"];
    const v = weekNum % 100;
    return (
      weekNum +
      (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]) +
      " week"
    );
  };

  useEffect(() => {
    if (!nodes || !nodes.length) return;

    // A temporary structure to hold aggregated data by month and a local week index
    const aggregatedData: any = {};
    const weeklyTotals: number[] = [];
    const monthWeekMapping: Record<string, number[]> = {};

    // First, process the data to get a list of unique week numbers for each month
    nodes.forEach((n) => {
      const match = n.week.match(/(\d{4}) (\w+) \(Week (\d+)\)/);
      if (!match) return;
      const [, year, month, weekNumStr] = match;
      const weekNum = parseInt(weekNumStr, 10);

      if (!monthWeekMapping[month]) {
        monthWeekMapping[month] = [];
      }
      if (!monthWeekMapping[month].includes(weekNum)) {
        monthWeekMapping[month].push(weekNum);
      }
    });

    // Sort the week numbers for each month to establish a consistent order
    for (const month in monthWeekMapping) {
      monthWeekMapping[month].sort((a, b) => a - b);
    }

    // Now, aggregate the data using the month-relative week index
    nodes.forEach((n) => {
      const match = n.week.match(/(\d{4}) (\w+) \(Week (\d+)\)/);
      if (!match) return;

      const [, year, month, weekNumStr] = match;
      const weekNum = parseInt(weekNumStr, 10);

      // Find the month-relative week index (1-5)
      const weekIndex = monthWeekMapping[month].indexOf(weekNum) + 1;

      if (weekIndex >= 1 && weekIndex <= 5) {
        if (!aggregatedData[month]) {
          aggregatedData[month] = {};
        }

        if (!aggregatedData[month][weekIndex]) {
          aggregatedData[month][weekIndex] = {
            saleCount: 0,
            total: 0,
            yearCount: 0,
          };
        }

        aggregatedData[month][weekIndex].saleCount += n.saleCount;
        aggregatedData[month][weekIndex].total += n.total;
        aggregatedData[month][weekIndex].yearCount += 1;
      }
    });

    // Calculate the final averages
    const finalAverages: any = {};
    monthOrder.forEach((month) => {
      if (aggregatedData[month]) {
        finalAverages[month] = {};
        for (const weekIndex in aggregatedData[month]) {
          const data = aggregatedData[month][weekIndex];
          const avgCount =
            data.yearCount > 0 ? data.saleCount / data.yearCount : 0;
          const avgTotal =
            data.yearCount > 0 ? data.total / data.yearCount : 0;

          finalAverages[month][weekIndex] = {
            saleCount: avgCount,
            total: avgTotal,
          };
          if(avgTotal!==0) {
          weeklyTotals.push(avgTotal);
          }
        }
      }
    });

    setWeeklyAverages(finalAverages);
    setWeeklyTotalValues(weeklyTotals);
  }, [nodes]);

  const getColor = (value: number, list: number[]) => {
    if (value === 0 || list.length === 0)
      return theme.palette.background.entity_background;
    const startColor = { r: 222, g: 255, b: 235 };
    const endColor = { r: 2, g: 105, b: 87 };
    const sorted = [...new Set(list)].sort((a, b) => a - b);
    const index = sorted.indexOf(value);
    const maxIndex = sorted.length - 1;
    const ratio = maxIndex === 0 ? 0 : index / maxIndex;
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio);
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio);
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const isDarkColor = (color: string): boolean => {
    let r = 0,
      g = 0,
      b = 0;
      if(color === "#FCFCFC") return false

    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    }
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 128;
  };

  return (
    <Box className={classes.tableWrapper}>
      <Table className={classes.table} size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              className={`${classes.headerCell} ${classes.stickyWeekCell}`}
            >
              Week
            </TableCell>
            {monthOrder.map((month) => (
              <TableCell
                key={month}
                className={classes.headerCell}
                align="center"
              >
                {month}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {weekRows.map((weekName, weekIndex) => (
            <TableRow key={weekName} className={classes.bodyRow}>
              <TableCell className={classes.stickyWeekCell}>
                {weekName}
              </TableCell>
              {monthOrder.map((month) => {
                const weekNum = weekIndex + 1; // Weeks are 1-indexed for the table rows
                const data = weeklyAverages[month]?.[weekNum] || {
                  saleCount: 0,
                  total: 0,
                };
                const bgColor = getColor(data.total, weeklyTotalValues);
                return (
                  <TableCell
                    key={`${month}-${weekNum}`}
                    align="center"
                    style={{
                      borderBottom: "none",
                      padding: "1px",
                      width: "1%", // 🔹 distribute evenly
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: bgColor,
                        borderRadius: "10px",
                        height: "42px",
                        width: "100%", // 🔹 now expands to fit evenly
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          color: isDarkColor(bgColor) ? "white" : "black",
                          fontWeight: "bold",
                        }}
                      >
                        {data.total > 0 ? data.total.toFixed(2) : "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: isDarkColor(bgColor) ? "white" : "black",
                        }}
                      >
                        {data.saleCount > 0 ? Math.round(data.saleCount) : "0"}
                      </Typography>
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ChartSalesTableWeekly;
